import { useCallback, useEffect, useState } from "react";

import type { GroupedPurposesBySection } from "@ui/components/ConsentSection";
import { useForm } from "@ui/components/Form";
import { useTranslations } from "@ui/providers/useTranslations";

import { dispatchCustomEvent, GlobalEvents, type OtPurposeByUUID } from "@jti/fe-common";
import { useUpdateConsents } from "@jti/rest-api";

import {
  type ConsentReminderFormValues,
  getConsentFormValues,
  getConsentUpdates,
  getUnconfirmedMandatoryConsents,
} from "../utils/consentReminder";

type UseConsentReminderFormProps = {
  displayedConsents: OtPurposeByUUID[];
  displayedConsentSections: GroupedPurposesBySection[];
  uuid?: string;
  refetchConsents: (options: { throwOnError: true }) => Promise<unknown>;
  closeConsentReminder: () => void;
};

export const useConsentReminderForm = ({
  displayedConsents,
  displayedConsentSections,
  uuid,
  refetchConsents,
  closeConsentReminder,
}: UseConsentReminderFormProps) => {
  const { __ } = useTranslations();
  const { updateConsents, isUpdating } = useUpdateConsents();
  const form = useForm<ConsentReminderFormValues>({ defaultValues: { consents: {} } });
  const [submissionError, setSubmissionError] = useState<string>();
  const isActionsBlocked = form.formState.isSubmitting || isUpdating;

  useEffect(() => {
    if (displayedConsentSections.length) {
      form.reset(getConsentFormValues(displayedConsentSections));
      setSubmissionError(undefined);
    }
  }, [displayedConsentSections, form]);

  const confirmChoices = useCallback(
    async ({ consents }: ConsentReminderFormValues) => {
      if (isActionsBlocked || !displayedConsents.length) return;

      const consentUpdates = getConsentUpdates(consents);
      const unconfirmedMandatoryConsents = getUnconfirmedMandatoryConsents(displayedConsents, consentUpdates);

      if (unconfirmedMandatoryConsents.length) {
        unconfirmedMandatoryConsents.forEach((consent) => {
          form.setError(`consents.${consent.id}`, {
            type: "required",
            message: __("com.communication.consent.required"),
          });
        });
        return;
      }

      try {
        setSubmissionError(undefined);
        await updateConsents({ consents: consentUpdates });

        if (uuid) {
          dispatchCustomEvent(GlobalEvents.ConsentsUpdated, { uuid });
        }

        await refetchConsents({ throwOnError: true });
        closeConsentReminder();
      } catch {
        setSubmissionError(__("com.communication.general_error"));
      }
    },
    [__, closeConsentReminder, displayedConsents, form, isActionsBlocked, refetchConsents, updateConsents, uuid]
  );

  return {
    form,
    submissionError,
    isActionsBlocked,
    confirmChoices,
    submit: form.handleSubmit(confirmChoices),
  };
};
